import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Ban,
  CheckCircle2,
  Link2,
  Lock,
  Plus,
  Send,
  Trash2,
  Type,
} from 'lucide-react';
import { PageHeader, ErrorState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, Separator, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions, useContactOptions } from '@/features/crm/api';
import { ApiRequestError } from '@/lib/api';
import { currencyLabel } from '@/lib/currencies';
import { formatDate, formatMoney } from '@/lib/format';
import { useSession } from '@/features/auth/use-session';
import {
  useApproveInvoice,
  useInvoice,
  useRateQuote,
  useSaveInvoice,
  useSendInvoice,
  useTaxRates,
  useVoidInvoice,
} from './api';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_VARIANT, INVOICE_TYPE_LABELS } from './labels';
import type { InvoiceType } from './types';

type DraftItem = {
  isHeading: boolean;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: string;
  discountType: 'PERCENT' | 'AMOUNT';
  discountValue: number;
  taxRateId: string;
};

const BLANK_ITEM: DraftItem = {
  isHeading: false,
  description: '',
  quantity: 1,
  unit: '',
  unitPrice: '',
  discountType: 'PERCENT',
  discountValue: 0,
  taxRateId: '',
};

const TYPES = Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[];

export function InvoiceBuilderPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const isNew = !invoiceId || invoiceId === 'new';
  const { data: invoice, isLoading, error, refetch } = useInvoice(isNew ? undefined : invoiceId);

  const save = useSaveInvoice();
  const approve = useApproveInvoice();
  const send = useSendInvoice();
  const voidInvoice = useVoidInvoice();

  const { data: companies = [] } = useCompanyOptions();
  const { data: taxRates = [] } = useTaxRates();
  const defaultTaxRateId = taxRates.find((rate) => rate.isDefault)?.id ?? '';

  const [companyId, setCompanyId] = useState(params.get('companyId') ?? '');
  const { data: contacts = [] } = useContactOptions(companyId || undefined);

  const [contactId, setContactId] = useState('');
  const [type, setType] = useState<InvoiceType>('STANDARD');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [poReference, setPoReference] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [withholdingTax, setWithholdingTax] = useState('');
  // Blank means "whatever the company bills in" — only a client with more than
  // one currency ever needs this set.
  const [currencyChoice, setCurrencyChoice] = useState('');
  const [items, setItems] = useState<DraftItem[]>([{ ...BLANK_ITEM }]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const [copied, setCopied] = useState(false);

  // Default the tax rate on a fresh line once the rates arrive.
  useEffect(() => {
    if (isNew && defaultTaxRateId) {
      setItems((current) =>
        current.map((item) => (item.taxRateId ? item : { ...item, taxRateId: defaultTaxRateId })),
      );
    }
  }, [isNew, defaultTaxRateId]);

  useEffect(() => {
    if (!invoice) return;
    setCompanyId(invoice.companyId);
    setCurrencyChoice(invoice.currency);
    setContactId(invoice.contactId ?? '');
    setType(invoice.type);
    setIssueDate(invoice.issueDate.slice(0, 10));
    setDueDate(invoice.dueDate.slice(0, 10));
    setPoReference(invoice.poReference ?? '');
    setNotes(invoice.notes ?? '');
    setTerms(invoice.terms ?? '');
    setWithholdingTax(invoice.withholdingTax ?? '');
    setItems(
      invoice.items.map((item) => ({
        isHeading: item.isHeading,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit ?? '',
        unitPrice: item.unitPrice ?? '',
        discountType: item.discountType,
        discountValue: item.discountValue,
        taxRateId: item.taxRateId ?? '',
      })),
    );
  }, [invoice]);

  if (!isNew && isLoading) {
    return (
      <div>
        <PageHeader title="Invoice" />
        <TableSkeleton />
      </div>
    );
  }
  if (!isNew && (error || !invoice)) {
    return <ErrorState message="Could not load this invoice." onRetry={() => void refetch()} />;
  }

  // Approval is the point of no return (spec INV-08).
  const locked = invoice ? !invoice.isDraft : false;
  const billTo = companies.find((option) => option.id === companyId);
  // The default first, then anything else this client is billed in.
  const billable = billTo ? [billTo.currency, ...billTo.currencies] : [];
  // What was chosen, else what this client is normally billed in.
  const currency = currencyChoice || billTo?.currency || 'AED';
  const totals = computeTotals(items, taxRates, withholdingTax);

  const setItem = (index: number, patch: Partial<DraftItem>) =>
    setItems((current) =>
      current.map((item, position) => (position === index ? { ...item, ...patch } : item)),
    );

  const submit = async () => {
    setSaveError(null);
    try {
      const saved = await save.mutateAsync({
        ...(invoice && !locked ? { id: invoice.id } : {}),
        type,
        companyId,
        currency,
        contactId,
        issueDate,
        dueDate,
        poReference,
        notes,
        terms,
        withholdingTax: withholdingTax || undefined,
        items: items
          .filter((item) => item.description.trim().length > 0)
          .map((item) => ({
            isHeading: item.isHeading,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice || '0',
            discountType: item.discountType,
            discountValue: item.discountValue,
            taxRateId: item.taxRateId || undefined,
          })),
      });
      navigate(`/finance/invoices/${saved.id}`);
    } catch (caught) {
      setSaveError(caught instanceof ApiRequestError ? caught.message : 'Could not save the invoice.');
    }
  };

  const publicLink = invoice ? `${window.location.origin}/i/${invoice.publicToken}` : '';

  return (
    <div>
      <PageHeader
        title={invoice ? (invoice.isDraft ? 'Draft invoice' : invoice.number) : 'New invoice'}
        breadcrumbs={[
          { label: 'Invoices', to: '/finance/invoices' },
          { label: invoice?.isDraft ? 'Draft' : (invoice?.number ?? 'New') },
        ]}
        description={invoice ? invoice.company.name : 'Bill a client.'}
        actions={
          invoice && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={INVOICE_STATUS_VARIANT[invoice.status]}>
                {INVOICE_STATUS_LABELS[invoice.status]}
              </Badge>

              {invoice.isDraft && (
                <Can permission="finance.invoice.approve">
                  <Button
                    size="sm"
                    disabled={approve.isPending}
                    onClick={async () => {
                      setSaveError(null);
                      try {
                        await approve.mutateAsync(invoice.id);
                      } catch (caught) {
                        setSaveError(
                          caught instanceof ApiRequestError ? caught.message : 'Could not approve.',
                        );
                      }
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {approve.isPending ? 'Approving…' : 'Approve'}
                  </Button>
                </Can>
              )}

              {locked && invoice.status !== 'VOID' && (
                <>
                  <Can permission="finance.invoice.send">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={send.isPending || !invoice.contactId}
                      title={invoice.contactId ? undefined : 'Add a billing contact with an email'}
                      onClick={() => send.mutate(invoice.id)}
                    >
                      <Send className="h-4 w-4" />
                      {send.isPending ? 'Sending…' : invoice.sentAt ? 'Resend' : 'Send'}
                    </Button>
                  </Can>
                  <Can permission="finance.invoice.approve">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmingVoid(true)}
                      disabled={invoice.amountPaid !== '0.00'}
                      title={
                        invoice.amountPaid !== '0.00'
                          ? 'This invoice has payments against it'
                          : undefined
                      }
                    >
                      <Ban className="h-4 w-4 text-destructive" />
                      Void
                    </Button>
                  </Can>
                </>
              )}
            </div>
          )
        }
      />

      {locked && invoice && invoice.status !== 'VOID' && (
        <div className="mb-5 flex items-start gap-2 rounded-md border bg-muted/40 px-4 py-2.5 text-sm">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <span>
            Approved on {formatDate(invoice.approvedAt)}
            {invoice.approvedBy ? ` by ${invoice.approvedBy.name}` : ''}, so it can no longer be
            edited. Correct it with a credit note, or void and reissue.
          </span>
        </div>
      )}

      {invoice?.status === 'VOID' && (
        <div className="mb-5 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Voided — {invoice.voidReason}. The ledger entry has been reversed.
        </div>
      )}

      {invoice?.sentAt && invoice.status !== 'VOID' && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-4 py-2.5 text-sm">
          <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <code className="min-w-0 flex-1 truncate text-xs">{publicLink}</code>
          <button
            onClick={() => {
              void navigator.clipboard.writeText(publicLink);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
            className="shrink-0 text-xs text-primary hover:underline"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          {invoice.viewCount > 0 && (
            <span className="shrink-0 text-xs text-muted-foreground">
              Opened {invoice.viewCount} {invoice.viewCount === 1 ? 'time' : 'times'}
            </span>
          )}
        </div>
      )}

      {confirmingVoid && invoice && (
        <div className="mb-5 rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm font-medium text-destructive">Void {invoice.number}?</p>
          <p className="mt-1 text-xs text-muted-foreground">
            The invoice keeps its number and stays on record; its ledger entry is reversed.
          </p>
          <Input
            className="mt-3"
            value={voidReason}
            placeholder="Why is it being voided?"
            onChange={(event) => setVoidReason(event.target.value)}
          />
          <div className="mt-3 flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              disabled={voidReason.trim().length < 3 || voidInvoice.isPending}
              onClick={async () => {
                await voidInvoice.mutateAsync({ id: invoice.id, reason: voidReason });
                setConfirmingVoid(false);
              }}
            >
              Void invoice
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmingVoid(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Bill to</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Company">
                <Select
                  value={companyId}
                  disabled={locked || Boolean(invoice)}
                  onChange={(event) => {
                    setCompanyId(event.target.value);
                    setContactId('');
                    setCurrencyChoice('');
                  }}
                >
                  <option value="">Choose a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Currency"
                hint={
                  billable.length > 1
                    ? 'This client is billed in more than one currency.'
                    : 'Set on the company record.'
                }
              >
                <Select
                  value={currency}
                  disabled={locked || billable.length < 2}
                  onChange={(event) => setCurrencyChoice(event.target.value)}
                >
                  {/* An invoice already raised in a currency keeps it, even if
                      the company has since been changed. */}
                  {!billable.includes(currency) && <option value={currency}>{currency}</option>}
                  {billable.map((code) => (
                    <option key={code} value={code}>
                      {currencyLabel(code)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Billing contact" hint="Where the invoice is emailed.">
                <Select
                  value={contactId}
                  disabled={locked || !companyId}
                  onChange={(event) => setContactId(event.target.value)}
                >
                  <option value="">Not set</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name}
                      {contact.email ? ` — ${contact.email}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Type">
                <Select
                  value={type}
                  disabled={locked}
                  onChange={(event) => setType(event.target.value as InvoiceType)}
                >
                  {TYPES.map((value) => (
                    <option key={value} value={value}>
                      {INVOICE_TYPE_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="PO reference">
                <Input
                  value={poReference}
                  disabled={locked}
                  onChange={(event) => setPoReference(event.target.value)}
                />
              </Field>
              <Field label="Issue date">
                <Input
                  type="date"
                  value={issueDate}
                  disabled={locked}
                  onChange={(event) => setIssueDate(event.target.value)}
                />
              </Field>
              <Field label="Due date" hint="Left blank, it follows the client payment terms.">
                <Input
                  type="date"
                  value={dueDate}
                  disabled={locked}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Lines</CardTitle>
              {!locked && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setItems((current) => [
                        ...current,
                        { ...BLANK_ITEM, isHeading: true, description: '' },
                      ])
                    }
                  >
                    <Type className="h-4 w-4" />
                    Heading
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setItems((current) => [
                        ...current,
                        { ...BLANK_ITEM, taxRateId: defaultTaxRateId },
                      ])
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Line
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((item, index) =>
                item.isHeading ? (
                  <div key={index} className="flex items-center gap-2 pt-2">
                    <Input
                      className="font-semibold"
                      placeholder="Section heading"
                      value={item.description}
                      disabled={locked}
                      onChange={(event) => setItem(index, { description: event.target.value })}
                    />
                    {!locked && (
                      <button
                        onClick={() => setItems((c) => c.filter((_, i) => i !== index))}
                        className="shrink-0 text-destructive"
                        aria-label="Remove heading"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div key={index} className="rounded-md border p-3">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      disabled={locked}
                      onChange={(event) => setItem(index, { description: event.target.value })}
                    />
                    <div className="mt-2 grid gap-2 sm:grid-cols-6">
                      <Small label="Qty">
                        <Input
                          type="number"
                          step="0.01"
                          min={0}
                          className="h-8"
                          value={item.quantity}
                          disabled={locked}
                          onChange={(event) =>
                            setItem(index, { quantity: Number(event.target.value) })
                          }
                        />
                      </Small>
                      <Small label="Unit price">
                        <Input
                          inputMode="decimal"
                          className="h-8"
                          value={item.unitPrice}
                          disabled={locked}
                          onChange={(event) => setItem(index, { unitPrice: event.target.value })}
                        />
                      </Small>
                      <Small label="Discount">
                        <div className="flex gap-1">
                          <Input
                            type="number"
                            min={0}
                            className="h-8"
                            value={item.discountValue}
                            disabled={locked}
                            onChange={(event) =>
                              setItem(index, { discountValue: Number(event.target.value) })
                            }
                          />
                          <Select
                            className="h-8 w-14 px-1 text-xs"
                            value={item.discountType}
                            disabled={locked}
                            onChange={(event) =>
                              setItem(index, {
                                discountType: event.target.value as 'PERCENT' | 'AMOUNT',
                              })
                            }
                          >
                            <option value="PERCENT">%</option>
                            <option value="AMOUNT">{currency}</option>
                          </Select>
                        </div>
                      </Small>
                      <Small label="Tax">
                        <Select
                          className="h-8 text-xs"
                          value={item.taxRateId}
                          disabled={locked}
                          onChange={(event) => setItem(index, { taxRateId: event.target.value })}
                        >
                          <option value="">No tax</option>
                          {taxRates
                            .filter((rate) => rate.isActive)
                            .map((rate) => (
                              <option key={rate.id} value={rate.id}>
                                {rate.name}
                              </option>
                            ))}
                        </Select>
                      </Small>
                      <Small label="Line total">
                        <div className="flex h-8 items-center justify-end tabular text-sm font-medium">
                          {formatMoney(lineTotal(item, taxRates), currency)}
                        </div>
                      </Small>
                      <div className="flex items-end justify-end">
                        {!locked && items.filter((i) => !i.isHeading).length > 1 && (
                          <button
                            onClick={() => setItems((c) => c.filter((_, i) => i !== index))}
                            className="pb-1.5 text-xs text-destructive hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Notes and terms</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Notes on the invoice">
                <Textarea
                  rows={3}
                  value={notes}
                  disabled={locked}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </Field>
              <Field label="Payment terms">
                <Textarea
                  rows={3}
                  value={terms}
                  disabled={locked}
                  onChange={(event) => setTerms(event.target.value)}
                />
              </Field>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="lg:sticky lg:top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Total</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Subtotal" value={formatMoney(totals.subtotal, currency)} />
              {totals.discount !== '0.00' && (
                <Row label="Discount" value={`− ${formatMoney(totals.discount, currency)}`} />
              )}
              <Row label="Taxable" value={formatMoney(totals.taxable, currency)} />
              <Row label="Tax" value={formatMoney(totals.tax, currency)} />

              <Field label="Withholding tax" hint="Deducted by the client at source.">
                <Input
                  inputMode="decimal"
                  className="h-8"
                  placeholder="0.00"
                  value={withholdingTax}
                  disabled={locked}
                  onChange={(event) => setWithholdingTax(event.target.value)}
                />
              </Field>

              <Separator />
              <div className="flex items-center justify-between text-base font-semibold">
                <span>Total</span>
                <span className="tabular">{formatMoney(totals.grand, currency)}</span>
              </div>
              <BaseEquivalent amount={totals.grand} from={currency} />
              {totals.withholding !== '0.00' && (
                <div className="text-xs text-muted-foreground">
                  Client pays {formatMoney(totals.payable, currency)} after withholding.
                </div>
              )}

              {invoice && !invoice.isDraft && (
                <>
                  <Separator />
                  <Row label="Paid" value={formatMoney(invoice.amountPaid, currency)} />
                  <div className="flex items-center justify-between font-medium">
                    <span>Outstanding</span>
                    <span className="tabular">{formatMoney(invoice.balanceDue, currency)}</span>
                  </div>
                </>
              )}

              <FormError message={saveError} />

              {!locked && (
                <Can permission="finance.invoice.create">
                  <Button
                    className="mt-2 w-full"
                    onClick={submit}
                    disabled={
                      save.isPending ||
                      !companyId ||
                      items.every((item) => item.isHeading || item.description.trim().length === 0)
                    }
                  >
                    {save.isPending ? 'Saving…' : invoice ? 'Save draft' : 'Create draft'}
                  </Button>
                </Can>
              )}
            </CardContent>
          </Card>

          {invoice?.payments && invoice.payments.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Payments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {invoice.payments.map((allocation) => (
                  <div key={allocation.id} className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {allocation.payment.number}
                      <span className="ml-1 text-xs">
                        {formatDate(allocation.payment.date)}
                      </span>
                    </span>
                    <span className="tabular">{formatMoney(allocation.amount, currency)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Totals, mirroring the server so the preview matches what is saved ────────

type Rate = { id: string; ratePercent: number };

function ratePercent(item: DraftItem, rates: Rate[]): number {
  return rates.find((rate) => rate.id === item.taxRateId)?.ratePercent ?? 0;
}

function toMinor(value: string): number {
  const parsed = Number((value || '0').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function fromMinor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function lineTotal(item: DraftItem, rates: Rate[]): string {
  if (item.isHeading) return '0.00';
  const gross = Math.round(toMinor(item.unitPrice) * item.quantity);
  const discount =
    item.discountType === 'AMOUNT'
      ? toMinor(String(item.discountValue))
      : Math.round((gross * item.discountValue) / 100);
  const taxable = gross - discount;
  return fromMinor(taxable + Math.round((taxable * ratePercent(item, rates)) / 100));
}

function computeTotals(items: DraftItem[], rates: Rate[], withholding: string) {
  let subtotal = 0;
  let discount = 0;
  let taxable = 0;
  let tax = 0;

  for (const item of items) {
    if (item.isHeading) continue;
    const gross = Math.round(toMinor(item.unitPrice) * item.quantity);
    const itemDiscount =
      item.discountType === 'AMOUNT'
        ? toMinor(String(item.discountValue))
        : Math.round((gross * item.discountValue) / 100);
    const itemTaxable = gross - itemDiscount;
    subtotal += gross;
    discount += itemDiscount;
    taxable += itemTaxable;
    tax += Math.round((itemTaxable * ratePercent(item, rates)) / 100);
  }

  const grand = taxable + tax;
  const withheld = toMinor(withholding);

  return {
    subtotal: fromMinor(subtotal),
    discount: fromMinor(discount),
    taxable: fromMinor(taxable),
    tax: fromMinor(tax),
    grand: fromMinor(grand),
    withholding: fromMinor(withheld),
    payable: fromMinor(grand - withheld),
  };
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Small({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

/**
 * What this invoice is worth in the organisation's own currency.
 *
 * Reporting happens in one currency; a client billed in another still has to
 * land somewhere in the accounts. The rate is the stored one, and the line
 * says so — it is a guide for the person raising the invoice, not the figure
 * the client is charged.
 */
function BaseEquivalent({ amount, from }: { amount: string; from: string }) {
  const { data: user } = useSession();
  const base = user?.organization.baseCurrency ?? 'AED';
  const { data: quote } = useRateQuote(amount, from, base, from !== base);

  if (from === base || !quote) return null;

  return (
    <div className="text-xs text-muted-foreground">
      ≈ {formatMoney(quote.converted, base)} at {quote.rate.toFixed(4)} {base} per {from}
    </div>
  );
}
